import React from 'react';
import { useTranslation } from 'react-i18next';

import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import { alpha } from '@mui/material/styles';

import Notification from '../util/Notification';

import Dropzone from './Dropzone';
import { mislecAPI } from '../../util/axios';
import { convertFileToBase64 } from '../util/file';

function DocumentUpload({ user, updateUser, reload, setModalState }) {
    //  State
    const [files, setFiles] = React.useState([]);
    const [data, setData] = React.useState([]);
    const [open, setOpen] = React.useState(false);
    const [message, setMessage] = React.useState(null);
    const [severity, setSeverity] = React.useState(null);

    const { t } = useTranslation();

    React.useEffect(() => {
        const fetchDocumentData = async () => {
            let documents = [];
            for (const file of files) {
                try {
                    //  Convert file to base64
                    const base64 = await convertFileToBase64(file);

                    //  Add document to list
                    documents.push({
                        original: base64,
                        filename: file.name,
                        type: file.type,
                        size: file.size,
                        user: user._id
                    });
                } catch (err) {
                    console.log(err);
                }
            }
            setData(documents);
        }

        fetchDocumentData();
    }, [files]);

    const upload = () => {
        if (files.length === 0) {
            setMessage(t('documents.modals.upload.error.noFiles'));
            setSeverity('warning');
            setOpen(true);
        }

        for (const doc of data) {
            // Request body
            const data = {
                original: doc.original,
                filename: doc.filename,
                type: doc.type,
                size: doc.size,
                user: user._id
            }

            // Send data to server
            mislecAPI.post(`/api/document`, data)
                .then(res => {
                    //  Notify user of successful upload
                    setMessage(t(res.data.detail));
                    setSeverity('success');
                    setOpen(true);

                    setTimeout(() => {
                        //  Clear files
                        setFiles([]);

                        //  Reload documents
                        reload();

                        //  Close modal
                        setModalState(false);
                    }, 2500);

                })
                .catch(error => {
                    //  Notify user of error
                    setMessage(error.response ? t(error.response.data.detail) : t('api.general.500'));
                    setSeverity('error');
                    setOpen(true);

                    //  Expired session
                    if (error.response)
                        if (error.response.data.detail === 'api.auth.verify.401')
                            updateUser(null);
                });
        }
    }

    return (
        <>
            <Dropzone fileList={files} onFilesSelected={setFiles} />
            <Stack
                direction='row'
                alignItems='center'
                mt={2}
                sx={{
                    justifyContent: {
                        xs: 'space-between',
                        md: 'flex-end'
                    }
                }}
            >
                <Button
                    variant=''
                    onClick={() => setModalState(false)}
                    sx={{
                        borderRadius: '8px !important',
                        backgroundColor: (theme) => ({
                            xs: alpha(theme.palette.error.main, 0.08),
                            md: 'rgba(33, 43, 54, 0.08)'
                        }),
                        color: {
                            xs: 'error.main',
                            md: 'inherit'
                        },
                        '&:hover': {
                            color: 'error.main',
                            backgroundColor: (theme) => alpha(theme.palette.error.main, 0.08)
                        }
                    }}
                >
                    {t('documents.modals.upload.buttons.cancel')}
                </Button>
                <Button
                    variant=''
                    onClick={() => upload()}
                    sx={{
                        ml: {
                            xs: 0,
                            md: 1
                        },
                        borderRadius: '8px !important',
                        backgroundColor: (theme) => ({
                            xs: alpha(theme.palette.primary.main, 0.08),
                            md: 'rgba(33, 43, 54, 0.08)'
                        }),
                        color: {
                            xs: 'primary.main',
                            md: 'inherit'
                        },
                        '&:hover': {
                            color: 'primary.main',
                            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08)
                        }
                    }}
                >
                    {t('documents.modals.upload.buttons.upload')}
                </Button>

            </Stack>
            <Notification open={open} message={message} severity={severity} onClose={() => setOpen(false)} />
        </>
    );
}

export default DocumentUpload;